import React, { useState, useEffect } from 'react';
import { structureAPI } from '../../services/api';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../../components/ui/sheet';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { toast } from 'sonner';
import { User, Building2, Users, Mail, Phone, Briefcase, X, Save, Trash2, UserPlus, UserMinus } from 'lucide-react';

const LEVEL_OPTIONS = [
  'Executive',
  'C-Suite',
  'Director',
  'Head',
  'Lead',
  'Manager',
  'Senior',
  'Staff'
];

const RoleDetailDrawer = ({ open, onClose, role, departments, people, onUpdate }) => {
  const [formData, setFormData] = useState({
    title: '',
    departmentId: '',
    levelLabel: '',
    responsibilities: '',
    assignedPersonId: '',
    parentRoleId: ''
  });
  const [saving, setSaving] = useState(false);
  const [isNew, setIsNew] = useState(true);

  useEffect(() => {
    if (role) {
      setFormData({
        title: role.title || '',
        departmentId: role.departmentId || '',
        levelLabel: role.levelLabel || '',
        responsibilities: role.responsibilities || '',
        assignedPersonId: role.assignedPersonId || '',
        parentRoleId: role.parentRoleId || ''
      });
      setIsNew(false);
    } else {
      setFormData({
        title: '',
        departmentId: departments[0]?.id || '',
        levelLabel: '',
        responsibilities: '',
        assignedPersonId: '',
        parentRoleId: ''
      });
      setIsNew(true);
    }
  }, [role, departments]);

  const handleChange = (field, value) => {
    // Handle "none" special value for person select
    if (field === 'assignedPersonId' && value === 'none') {
      value = '';
    }
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      toast.error('Role title is required');
      return;
    }
    if (!formData.departmentId) {
      toast.error('Department is required');
      return;
    }

    try {
      setSaving(true);
      
      const payload = {
        title: formData.title,
        departmentId: formData.departmentId,
        levelLabel: formData.levelLabel || null,
        responsibilities: formData.responsibilities || null,
        assignedPersonId: formData.assignedPersonId || null,
        parentRoleId: formData.parentRoleId || null,
        scopeType: 'company'
      };

      if (isNew) {
        await structureAPI.createRole(payload);
        toast.success('Role created successfully');
      } else {
        await structureAPI.updateRole(role.id, payload);
        toast.success('Role updated successfully');
      }
      
      onUpdate();
      onClose();
    } catch (error) {
      console.error('Error saving role:', error);
      toast.error('Failed to save role');
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async () => {
    if (!role) return;
    
    try {
      setSaving(true);
      await structureAPI.archiveRole(role.id);
      toast.success('Role archived successfully');
      onUpdate();
      onClose();
    } catch (error) {
      console.error('Error archiving role:', error);
      toast.error('Failed to archive role');
    } finally {
      setSaving(false);
    }
  };

  const handleUnassign = async () => {
    if (!role) return;
    
    try {
      setSaving(true);
      await structureAPI.updateRole(role.id, {
        ...formData,
        assignedPersonId: null
      });
      toast.success('Person unassigned from role');
      onUpdate();
      onClose();
    } catch (error) {
      console.error('Error unassigning person:', error);
      toast.error('Failed to unassign person');
    } finally {
      setSaving(false);
    }
  };

  // Filter out people who are already assigned to other roles
  const availablePeople = people.filter(p => 
    p.status === 'active' && 
    (p.id === formData.assignedPersonId || !role?.assignedPersonId || role?.assignedPersonId !== p.id)
  );

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="sm:max-w-lg overflow-y-auto" data-testid="role-detail-drawer">
        <SheetHeader className="mb-6">
          <SheetTitle className="flex items-center gap-2">
            <Briefcase className="w-5 h-5" style={{ color: '#2A9D8F' }} />
            {isNew ? 'Create New Role' : 'Edit Role'}
          </SheetTitle>
          <SheetDescription>
            {isNew 
              ? 'Add a new role to your organization structure'
              : 'Update role details and assignments'
            }
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-6">
          {/* Role Title */}
          <div className="space-y-2">
            <Label htmlFor="title">Role Title *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder="e.g., Head of Engineering"
              data-testid="role-title-input"
            />
          </div>

          {/* Department */}
          <div className="space-y-2">
            <Label>Department *</Label>
            <Select
              value={formData.departmentId}
              onValueChange={(value) => handleChange('departmentId', value)}
            >
              <SelectTrigger data-testid="role-department-select">
                <SelectValue placeholder="Select department" />
              </SelectTrigger>
              <SelectContent>
                {departments.map(dept => (
                  <SelectItem key={dept.id} value={dept.id}>
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4" style={{ color: '#6B7280' }} />
                      {dept.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Level */}
          <div className="space-y-2">
            <Label>Level</Label>
            <Select
              value={formData.levelLabel}
              onValueChange={(value) => handleChange('levelLabel', value)}
            >
              <SelectTrigger data-testid="role-level-select">
                <SelectValue placeholder="Select level" />
              </SelectTrigger>
              <SelectContent>
                {LEVEL_OPTIONS.map(level => (
                  <SelectItem key={level} value={level}>
                    {level}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Separator />

          {/* Assigned Person */}
          <div className="space-y-2">
            <Label className="flex items-center justify-between">
              <span>Assigned Person</span>
              {formData.assignedPersonId && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleUnassign}
                  className="h-auto p-1 text-xs"
                  style={{ color: '#EF4444' }}
                  data-testid="unassign-person-btn"
                >
                  <UserMinus className="w-3 h-3 mr-1" />
                  Unassign
                </Button>
              )}
            </Label>
            <Select
              value={formData.assignedPersonId || 'none'}
              onValueChange={(value) => handleChange('assignedPersonId', value)}
            >
              <SelectTrigger data-testid="role-person-select">
                <SelectValue placeholder="Select person (optional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">
                  <span className="text-gray-400">None (Vacant)</span>
                </SelectItem>
                {availablePeople.map(person => (
                  <SelectItem key={person.id} value={person.id}>
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium"
                        style={{ backgroundColor: '#E8F4F2', color: '#2A9D8F' }}
                      >
                        {person.displayName?.split(' ').map(n => n[0]).join('') || 'U'}
                      </div>
                      <span>{person.displayName}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {formData.assignedPersonId && (
              <p className="text-xs" style={{ color: '#6B7280' }}>
                This role will be marked as filled
              </p>
            )}
            {!formData.assignedPersonId && (
              <Badge 
                variant="outline" 
                className="mt-1"
                style={{ backgroundColor: '#FEF3C7', color: '#92400E', borderColor: '#FCD34D' }}
              >
                Role will be marked as Vacant
              </Badge>
            )}
          </div>

          {/* Responsibilities */}
          <div className="space-y-2">
            <Label htmlFor="responsibilities">Responsibilities</Label>
            <Textarea
              id="responsibilities"
              value={formData.responsibilities}
              onChange={(e) => handleChange('responsibilities', e.target.value)}
              placeholder="Describe the key responsibilities for this role..."
              rows={4}
              data-testid="role-responsibilities-input"
            />
          </div>

          {/* Current Person Info (if viewing existing role with assigned person) */}
          {!isNew && role?.assignedPerson && (
            <>
              <Separator />
              <div className="space-y-3">
                <Label className="text-sm font-medium">Current Assignment</Label>
                <div 
                  className="p-4 rounded-lg"
                  style={{ backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB' }}
                >
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold"
                      style={{ backgroundColor: '#E8F4F2', color: '#2A9D8F' }}
                    >
                      {role.assignedPerson.displayName?.split(' ').map(n => n[0]).join('') || 'U'}
                    </div>
                    <div>
                      <p className="font-medium" style={{ color: '#1F2937' }}>
                        {role.assignedPerson.displayName}
                      </p>
                      {role.assignedPerson.email && (
                        <p className="text-sm flex items-center gap-1" style={{ color: '#6B7280' }}>
                          <Mail className="w-3 h-3" />
                          {role.assignedPerson.email}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-3 mt-8 pt-6 border-t">
          {!isNew && (
            <Button
              variant="outline"
              onClick={handleArchive}
              disabled={saving}
              className="flex items-center gap-2"
              style={{ color: '#EF4444', borderColor: '#FCA5A5' }}
              data-testid="archive-role-btn"
            >
              <Trash2 className="w-4 h-4" />
              Archive
            </Button>
          )}
          <div className="flex-1" />
          <Button
            variant="outline"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            style={{ backgroundColor: '#2A9D8F', color: '#FFFFFF' }}
            data-testid="save-role-btn"
          >
            {saving ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                {isNew ? 'Create Role' : 'Save Changes'}
              </>
            )}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default RoleDetailDrawer;
